import React, { useCallback, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Trash2,
  TrendingUp,
  Calendar as CalendarIcon,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Twitter,
  Sparkles,
  Edit2,
  Globe,
  Clock,
} from 'lucide-react';
import CardComponent from '../ui/CardComponent';
import ButtonComponent from '../ui/ButtonComponent';
import ContentsService from '../../../content-creation/services/contents.services';
import ContentMarketingService from '../../services/contentMarketing.services';
import { CalendarView, MarketingListParams, Platform, ScheduledPost } from '../../../../data/types/contentMarketing.interface';


const STORAGE_KEY = 'scheduledPosts';

const DEFAULT_FILTERS: MarketingListParams = {
  perPage: 50,
  page: 1,
  type: 'final',
};

export default function Calendar() {
  const queryClient = useQueryClient();
  const cacheRef = useRef<Map<string, string>>(new Map());
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<CalendarView>('combined');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [hoveredPost, setHoveredPost] = useState<string | null>(null);
  const [uploadedMedia, setUploadedMedia] = useState<File | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    time: '09:00',
    platform: [] as Platform[],
    hashtags: '',
  });

  const PLATFORM_LIST: Platform[] = ['Instagram', 'Facebook', 'LinkedIn', 'YouTube', 'Twitter'];
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const fetchRealContent = useCallback(async (url: string) => {
    if (!url) return '';

    if (cacheRef.current.has(url)) {
      return cacheRef.current.get(url)!;
    }

    let resolved = '';

    if (url.includes('localhost')) {
      const res = await ContentsService.fetchRealS3Content(url);
      resolved = res.url;
    } else {
      resolved = await ContentsService.fetchRealDropboxContent(url);
    }

    cacheRef.current.set(url, resolved);
    return resolved;
  }, []);

  const { data: scheduledPosts = [], isLoading } = useQuery({
    queryKey: ['scheduled-posts'],
    queryFn: async (): Promise<ScheduledPost[]> => {
      const result = await ContentMarketingService.getContents({
        ...DEFAULT_FILTERS,
        scheduledPost: true,
      });

      const items = await Promise.all(
        result.list.map(async (item: any) => ({
          title: item.title,
          platform: ['Instagram'],
          status: 'scheduled',
          ...item,
          videoUrl: await fetchRealContent(item.videoUrl),
        }))
      );

      const local = localStorage.getItem(STORAGE_KEY);
      const localPosts = local ? JSON.parse(local) : items;
      return localPosts;
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const addPostMutation = useMutation({
    mutationFn: async (payload: ScheduledPost) => payload,
    onSuccess: (payload) => {
      const updated = [...scheduledPosts, payload];

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      queryClient.setQueryData(['scheduled-posts'], updated);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => id,
    onSuccess: (id) => {
      const updated = scheduledPosts.filter((item) => item.id !== id);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      queryClient.setQueryData(['scheduled-posts'], updated);
    },
  });

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });


  const useHolidays = (year: number) => {
    const indiaQuery = useQuery({
      queryKey: ['holidays', 'IN', year],
      queryFn: () => ContentMarketingService.fetchHolidays('IN', year),
      staleTime: 1000 * 60 * 60 * 24
    });

    const usQuery = useQuery({
      queryKey: ['holidays', 'US', year],
      queryFn: () => ContentMarketingService.fetchHolidays('US', year),
      staleTime: 1000 * 60 * 60 * 24,
    });

    const indianFestivals = ContentMarketingService.convertHolidayMap(indiaQuery.data || [], '#F67570', 'India');
    const usFestivals = ContentMarketingService.convertHolidayMap(usQuery.data || [], '#4A90E2', 'US');

    return {
      indianFestivals,
      usFestivals,
      isDataLoading: indiaQuery.isLoading || usQuery.isLoading,
      error: indiaQuery.error || usQuery.error,
    };
  };

  const { indianFestivals, usFestivals } = useHolidays(year);

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatDateKey = (day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getEventsForDate = (day: number) => {
    const dateKey = formatDateKey(day);
    const events: any[] = [];

    if (calendarView === 'india' || calendarView === 'combined') {
      if (indianFestivals[dateKey as keyof typeof indianFestivals]) {
        events.push({ ...indianFestivals[dateKey], source: 'india' });
      }
    }

    if (calendarView === 'us' || calendarView === 'combined') {
      if (usFestivals[dateKey as keyof typeof usFestivals]) {
        events.push({ ...usFestivals[dateKey], source: 'us' });
      }
    }

    return events;
  };

  const getPostsForDate = (day: number) => {
    const dateKey = formatDateKey(day);
    return scheduledPosts.filter((post) => post.date === dateKey);
  };

  const isWeekend = (day: number) => {
    const date = new Date(year, month, day);
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const isToday = (day: number) => {
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const handleDateClick = (day: number) => {
    const dateKey = formatDateKey(day);
    setSelectedDate(dateKey);
    setDrawerOpen(true);
  };

  /* Direct schedule post is not supported yet */
  const handleSchedulePost = () => {
    if (!selectedDate || !newPost.title) return;

    addPostMutation.mutate({
      id: `${Date.now()}`,
      date: selectedDate,
      time: newPost.time,
      title: newPost.title,
      platform: newPost.platform ?? 'YouTube',
      status: 'scheduled',
      mediaPreview: mediaPreviewUrl || undefined,
    });

    setNewPost({ title: '', time: '09:00', platform: ['YouTube'], hashtags: '' });
    setUploadedMedia(null);
    setMediaPreviewUrl(null);
    setDrawerOpen(false);
    setSelectedDate(null);
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'Instagram':
        return <Instagram size={12} />;
      case 'Facebook':
        return <Facebook size={12} />;
      case 'LinkedIn':
        return <Linkedin size={12} />;
      case 'YouTube':
        return <Youtube size={12} />;
      case 'Twitter':
        return <Twitter size={12} />;
      default:
        return null;
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'Instagram':
        return '#E4405F';
      case 'Facebook':
        return '#1877F2';
      case 'LinkedIn':
        return '#0A66C2';
      case 'YouTube':
        return '#FF0000';
      case 'Twitter':
        return '#1DA1F2';
      default:
        return '#6B7280';
    }
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'video/mp4'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload PNG, JPG, or MP4 files only');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadedMedia(file);

    // Create preview URL
    const url = URL.createObjectURL(file);
    setMediaPreviewUrl(url);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const removeMedia = () => {
    if (mediaPreviewUrl) {
      URL.revokeObjectURL(mediaPreviewUrl);
    }
    setUploadedMedia(null);
    setMediaPreviewUrl(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const calculateSEOScore = (hashtags: string) => {
    if (!hashtags.trim()) return 0;

    // Extract hashtags (words starting with #)
    const hashtagArray = hashtags.match(/#[\w]+/g) || [];
    const count = hashtagArray.length;

    // Calculate score based on optimal hashtag count (5-10 is ideal)
    let score = 0;
    if (count === 0) {
      score = 0;
    } else if (count >= 1 && count <= 3) {
      score = count * 20; // 20, 40, 60
    } else if (count >= 4 && count <= 7) {
      score = 60 + (count - 3) * 10; // 70, 80, 90, 100
    } else if (count >= 8 && count <= 10) {
      score = 100; // Optimal range
    } else {
      // More than 10 starts to decrease (spam-like)
      score = Math.max(70, 100 - (count - 10) * 5);
    }

    return Math.min(100, Math.max(0, score));
  };

  const getSEOScoreColor = (score: number) => {
    if (score >= 80) return { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' };
    if (score >= 60) return { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-200' };
    if (score >= 40) return { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' };
    return { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' };
  };

  const seoScore = calculateSEOScore(newPost.hashtags);
  const seoColors = getSEOScoreColor(seoScore);

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-semibold text-[#111827]">Post Scheduler Dashboard</h1>
            <p className="text-sm text-[#6B7280] mt-1">Plan content with cultural and market relevance</p>
          </div>
          <ButtonComponent variant="primary" size="md" onClick={() => setDrawerOpen(true)} hidden>
            <Plus size={16} className="mr-2" />
            Schedule Post
          </ButtonComponent>
        </div>
      </div>

      {/* Calendar View Toggle */}
      <CardComponent className="mb-4" padding="default">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Globe size={18} className="text-[#6B7280]" />
            <span className="text-sm font-medium text-[#111827]">Calendar View:</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCalendarView('india')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${calendarView === 'india'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
                  }`}
              >
                <span role="img" aria-label="india">🇮🇳</span> India Only
              </button>
              <button
                onClick={() => setCalendarView('us')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${calendarView === 'us'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
                  }`}
              >
                <span role="img" aria-label="usOnly">🇺🇸</span> US Only
              </button>
              <button
                onClick={() => setCalendarView('combined')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${calendarView === 'combined'
                  ? 'bg-gradient-to-r from-[#F67570] to-[#F99B97] text-white shadow-md'
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
                  }`}
              >
                <span role="img" aria-label="combinedView">🌍</span> Combined View
              </button>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-[#6B7280]">India Festivals</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-[#6B7280]">US Holidays</span>
            </div>
          </div>
        </div>
      </CardComponent>

      {/* Calendar Controls */}
      <CardComponent className="mb-6" padding="default">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button onClick={previousMonth} className="p-2 hover:bg-[#F9FAFB] rounded transition-colors">
                <ChevronLeft size={20} className="text-[#6B7280]" />
              </button>
              <div className="text-lg font-semibold text-[#111827] min-w-[200px] text-center">{monthName}</div>
              <button onClick={nextMonth} className="p-2 hover:bg-[#F9FAFB] rounded transition-colors">
                <ChevronRight size={20} className="text-[#6B7280]" />
              </button>
            </div>
            <ButtonComponent variant="secondary" size="sm" onClick={goToToday}>
              Today
            </ButtonComponent>
          </div>

          <div className="flex items-center gap-2">
            <CalendarIcon size={16} className="text-[#6B7280]" />
            <span className="text-sm text-[#6B7280]">{scheduledPosts.length} posts scheduled this month</span>
          </div>
        </div>
      </CardComponent>

      {/* Calendar Grid */}
      <CardComponent padding="default">
        {isLoading ? (
          <div className="p-10 text-center">Loading...</div>
        ) : (
          <>
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-3 mb-3">
              {DAYS.map((day) => (
                <div key={day} className="text-center text-xs font-semibold text-[#6B7280] uppercase tracking-wider py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-3">
              {/* Empty cells for days before month starts */}
              {Array.from({ length: startingDayOfWeek }).map((_, index) => (
                <div key={`empty-${index}`} className="min-h-[140px] bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]" />
              ))}

              {/* Days of the month */}
              {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1;
                const events = getEventsForDate(day);
                const posts = getPostsForDate(day);
                const weekend = isWeekend(day);
                const today = isToday(day);

                return (
                  <div
                    key={day}
                    onClick={() => handleDateClick(day)}
                    className={`min-h-[140px] rounded-lg border-2 p-2 cursor-pointer transition-all hover:shadow-md ${today
                      ? 'border-[#F67570] bg-[#FEF2F2] shadow-sm'
                      : weekend
                        ? 'border-[#E5E7EB] bg-[#FAFAFA]'
                        : 'border-[#E5E7EB] bg-white hover:border-[#D1D5DB]'
                      }`}
                  >
                    {/* Date Number & Indicators */}
                    <div className="flex items-start justify-between mb-2">
                      <div className={`text-sm font-semibold ${today ? 'text-[#F67570]' : 'text-[#111827]'}`}>{day}</div>
                    </div>

                    {/* Festival Tags */}
                    <div className="space-y-1 mb-2">
                      {events.map((event, idx) => (
                        <div
                          key={idx}
                          className="text-[10px] font-medium px-1.5 py-0.5 rounded truncate"
                          style={{
                            backgroundColor: `${event.color}15`,
                            color: event.color,
                            border: `1px solid ${event.color}40`,
                          }}
                          title={event.name}
                        >
                          {event.name}
                        </div>
                      ))}
                    </div>

                    {/* Scheduled Posts */}
                    <div className="space-y-1">
                      {posts.slice(0, 2).map((post) => (
                        <div
                          key={post.id}
                          className="relative group"
                          onMouseEnter={() => setHoveredPost(post.id)}
                          onMouseLeave={() => setHoveredPost(null)}
                        >
                          <div
                            className="text-[10px] px-1.5 py-1 rounded border cursor-pointer transition-all"
                            style={{
                              backgroundColor: `${getPlatformColor(post.platform[0])}10`,
                              borderColor: `${getPlatformColor(post.platform[0])}40`,
                              color: getPlatformColor(post.platform[0]),
                            }}
                          >
                            <div className="flex items-center gap-1 mb-0.5">
                              {getPlatformIcon(post.platform[0])}
                              <span className="font-medium">{post.time}</span>
                            </div>
                            <div className="truncate font-medium">{post.title}</div>
                          </div>

                          {/* Hover Actions */}
                          {hoveredPost === post.id && (
                            <div className="absolute right-0 top-0 flex gap-1 z-20" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="w-5 h-5 rounded bg-white border border-[#E5E7EB] shadow-sm hover:bg-blue-50 hover:border-blue-300 flex items-center justify-center transition-colors"
                                title="Edit"
                              >
                                <Edit2 size={10} className="text-blue-600" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteMutation.mutate(post.id);
                                }}
                                className="w-5 h-5 rounded bg-white border border-[#E5E7EB] shadow-sm hover:bg-red-50 hover:border-red-300 flex items-center justify-center transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={10} className="text-red-600" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      {posts.length > 2 && (
                        <div className="text-[10px] text-[#6B7280] font-medium px-1.5">+{posts.length - 2} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>)}
      </CardComponent>

      {/* Schedule Post Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={() => setDrawerOpen(false)} />

          {/* Drawer */}
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-[500px] bg-white shadow-2xl flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-[#111827]">Schedule New Post</h2>
                  {selectedDate && (
                    <p className="text-sm text-[#6B7280] mt-1">
                      {new Date(selectedDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="w-8 h-8 rounded-lg hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X size={20} className="text-[#6B7280]" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Post Title */}
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Post Title *</label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  placeholder="Enter post title..."
                  className="w-full h-10 px-3 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-1 focus:ring-[#F67570]"
                />
              </div>

              {/* Hashtags & SEO Score */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-[#111827]">Hashtags</label>
                  <div
                    className={`flex items-center gap-2 px-3 py-1 rounded-full border ${seoColors.border} ${seoColors.bg}`}
                  >
                    <TrendingUp size={14} className={seoColors.text} />
                    <span className={`text-xs font-semibold ${seoColors.text}`}>SEO Score: {seoScore}%</span>
                  </div>
                </div>
                <textarea
                  value={newPost.hashtags}
                  onChange={(e) => setNewPost({ ...newPost, hashtags: e.target.value })}
                  placeholder="Add hashtags (e.g., #marketing #socialmedia #content)"
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-1 focus:ring-[#F67570] resize-none"
                />
                <p className="text-xs text-[#6B7280] mt-1.5">
                  {(() => {
                    const count = (newPost.hashtags.match(/#[\w]+/g) || []).length;
                    if (count === 0) return 'Add hashtags to improve SEO score (Recommended: 5-10)';
                    if (count >= 1 && count <= 4)
                      return `${count} hashtag${count > 1 ? 's' : ''} added. Add ${5 - count} more for optimal SEO.`;
                    if (count >= 5 && count <= 10) return `${count} hashtags - Optimal for SEO! 🎯`;
                    return `${count} hashtags - Consider reducing to 8-10 for best results.`;
                  })()}
                </p>
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">
                  <Clock size={14} className="inline mr-1" />
                  Scheduling Time *
                </label>
                <input
                  type="time"
                  value={newPost.time}
                  onChange={(e) => setNewPost({ ...newPost, time: e.target.value })}
                  className="w-full h-10 px-3 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F67570] focus:ring-1 focus:ring-[#F67570]"
                />
              </div>

              {/* Platform */}
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">
                  Platforms * <span className="text-xs font-normal text-[#6B7280]">(Select any one)</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {PLATFORM_LIST.map((platform) => {
                    const platforms = newPost.platform;
                    const isSelected = platforms.includes(platform);

                    return (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => {
                          const currentPlatforms = newPost.platform;
                          let updatedPlatforms;
                          if (isSelected) {
                            updatedPlatforms = currentPlatforms.filter((p) => p !== platform);
                          } else {
                            updatedPlatforms = [...currentPlatforms, platform];
                          }

                          setNewPost({ ...newPost, platform: updatedPlatforms.join(',') as any });
                        }}
                        className={`p-3 rounded-lg border-2 transition-all relative ${isSelected
                          ? 'border-[#F67570] bg-[#FEF2F2]'
                          : 'border-[#E5E7EB] bg-white hover:border-[#D1D5DB]'
                          }`}
                      >
                        <div className="flex items-center gap-2">
                          <div style={{ color: getPlatformColor(platform) }}>{getPlatformIcon(platform)}</div>
                          <span className="text-sm font-medium text-[#111827]">{platform}</span>
                        </div>

                        {/* Checkmark */}
                        {isSelected && (
                          <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#F67570] flex items-center justify-center">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M10 3L4.5 8.5L2 6"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Media Preview */}
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Media Preview (Optional)</label>

                {!uploadedMedia ? (
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('media-upload-input')?.click()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${isDragging ? 'border-[#F67570] bg-[#FEF2F2]' : 'border-[#E5E7EB] hover:border-[#D1D5DB] bg-white'
                      }`}
                  >
                    <div
                      className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center transition-colors ${isDragging ? 'bg-[#F67570]' : 'bg-[#F9FAFB]'
                        }`}
                    >
                      <Plus size={24} className={isDragging ? 'text-white' : 'text-[#6B7280]'} />
                    </div>
                    <p className="text-sm text-[#6B7280]">
                      {isDragging ? 'Drop file here' : 'Upload media or drag and drop'}
                    </p>
                    <p className="text-xs text-[#9CA3AF] mt-1">PNG, JPG, MP4 up to 10MB</p>
                    <input
                      id="media-upload-input"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,video/mp4"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="border-2 border-[#E5E7EB] rounded-lg overflow-hidden">
                    {/* Preview */}
                    <div className="bg-gray-100 p-4 flex items-center justify-center min-h-[120px]">
                      {uploadedMedia.type.startsWith('image/') ? (
                        <img src={mediaPreviewUrl || ''} alt="Preview" className="max-h-[200px] rounded" />
                      ) : (
                        <video src={mediaPreviewUrl || ''} className="max-h-[200px] rounded" controls />
                      )}
                    </div>

                    {/* File Info */}
                    <div className="p-3 bg-white border-t border-[#E5E7EB]">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#111827] truncate">{uploadedMedia.name}</p>
                          <p className="text-xs text-[#6B7280] mt-0.5">
                            {formatFileSize(uploadedMedia.size)} • {uploadedMedia.type.split('/')[1].toUpperCase()}
                          </p>
                        </div>
                        <button
                          onClick={removeMedia}
                          className="ml-3 p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors flex-shrink-0"
                          title="Remove media"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Festival Context (if applicable) */}
              {selectedDate && getEventsForDate(parseInt(selectedDate.split('-')[2])).length > 0 && (
                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Sparkles size={16} className="text-amber-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#111827] mb-1">Festival Opportunity</h4>
                      <p className="text-xs text-[#6B7280]">
                        This date has {getEventsForDate(parseInt(selectedDate.split('-')[2]))[0].name}. Consider
                        creating culturally relevant content!
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-[#E5E7EB] bg-[#F9FAFB]">
              <div className="flex items-center gap-3">
                <ButtonComponent variant="secondary" size="md" className="flex-1" onClick={() => setDrawerOpen(false)}>
                  Cancel
                </ButtonComponent>
                <ButtonComponent
                  variant="primary"
                  size="md"
                  className="flex-1"
                  onClick={handleSchedulePost}
                  disabled={!newPost.title}
                >
                  Schedule Post
                </ButtonComponent>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
